import { FormHelperText, MenuItem, Select, ThemeProvider } from '@mui/material'
import { ForwardedRef, forwardRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useAppSelector } from '~/app/hooks'
import { currentTheme, isDarkTheme } from '~/redux/features'
import { createCustomizedTheme, CustomizedSelect, MenuProps } from '.'
import classNames from 'classnames/bind'
import styles from './EFSelect.module.scss'
import DOMPurify from 'dompurify'
import { EFTypography } from '../EFTypography'
import { EFInputLabel } from '../EFInputLabel'
import { EFSelectProps } from './EFSelect.model'

const cx = classNames.bind(styles)
export const SelectForm = forwardRef(
  (props: EFSelectProps, _ref: ForwardedRef<unknown>) => {
    const { label, name, data, value, errorMessage, onChange, ...selectProps } = props
    const theme = useAppSelector(currentTheme)
    const isDark = useAppSelector(isDarkTheme)
    const { control, setValue } = useFormContext() ?? {}

    if (!control) return <CustomizedSelect {...props} />
    return (
      <Controller
        control={control}
        defaultValue={props.defaultValue}
        name={props.name}
        rules={ {required: props.required ?? false}}
        render={({ field, fieldState }) => (
          <div className='custom-field'>
            {label && (
              <EFInputLabel
                name={field.name}
                label={label}
              />
            )}
            <ThemeProvider theme={createCustomizedTheme(isDark)}>
              <Select
                {...field}
                {...selectProps}
                defaultValue={''}
                name={field.name}
                value={field.value ?? ''}
                labelId={field.name}
                id={field.name}
                MenuProps={{ ...MenuProps, ...props.MenuProps }}
                className={cx('select-field', theme)}
                displayEmpty
                onChange={event => {
                  const { value } = event.target
                  setValue(field.name, value, {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true
                  })
                  onChange?.(event)
                }}
                error={fieldState.invalid === true}
              >
                {props.placeholder && (
                  <MenuItem
                    value={''}
                    className={cx('menu-select-item', theme)}
                  >
                    <EFTypography color='secondary'>{props.placeholder}</EFTypography>
                  </MenuItem>
                )}
                {data.map(item => (
                  <MenuItem
                    className={cx('menu-select-item', theme)}
                    key={item.value}
                    value={item.value}
                    disabled={item.isDisabled}
                    hidden={item.isHidden}
                  >
                    <span
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.name) }}
                    />
                  </MenuItem>
                ))}
              </Select>
              {errorMessage && (
                <FormHelperText sx={{ color: 'red !important' }}>
                  {errorMessage}
                </FormHelperText>
              )}
            </ThemeProvider>
          </div>
        )}
      />
    )
  }
)
